/**
 * Hook für die mandantenübergreifende Anlagentyp-Verwaltung.
 *
 * Der Anlagentyp ist die Vorlage, aus der beim Hinzufügen einer Anlage ins Projekt kopiert wird.
 * Änderungen wirken deshalb **nicht** rückwirkend: Laufende Projekte tragen ihre Kopie vom
 * Zeitpunkt des Anlegens.
 *
 * Stoffliste und Betriebsmodi werden je Anlagentyp nachgeladen, nicht mit der Liste zusammen –
 * sie werden erst gebraucht, wenn ein Typ zum Bearbeiten geöffnet wird.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Anlagentyp,
  AnlagentypBetriebsmodus,
  AnlagentypCreate,
  AnlagentypStoff,
  AnlagentypUpdate,
  BetriebsmodusCreate,
  BetriebsmodusUpdate,
  Hersteller,
  StoffCreate,
  StoffUpdate,
  anlagentypenApi,
} from "../../../../services/api/anlagentypen";
import { useLoadState } from "../../../../hooks/useLoadState";

export const useAdminAnlagentypen = () => {
  const [anlagentypen, setAnlagentypen] = useState<Anlagentyp[]>([]);
  const [hersteller, setHersteller] = useState<Hersteller[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { loading, ladenStarten, ladenBeenden } = useLoadState();

  const load = useCallback(async () => {
    ladenStarten();
    setError(null);
    try {
      const [typen, marken] = await Promise.all([
        anlagentypenApi.getAnlagentypen(),
        anlagentypenApi.getHersteller(),
      ]);
      setAnlagentypen(typen);
      setHersteller(marken);
    } catch {
      setError("Anlagentypen konnten nicht geladen werden");
    } finally {
      ladenBeenden();
    }
  }, [ladenStarten, ladenBeenden]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = useCallback(
    async (daten: AnlagentypCreate) => {
      const angelegt = await anlagentypenApi.createAnlagentyp(daten);
      await load();
      return angelegt;
    },
    [load],
  );

  const update = useCallback(
    async (id: string, daten: AnlagentypUpdate) => {
      await anlagentypenApi.updateAnlagentyp(id, daten);
      await load();
    },
    [load],
  );

  const remove = useCallback(
    async (id: string) => {
      await anlagentypenApi.deleteAnlagentyp(id);
      await load();
    },
    [load],
  );

  // Hersteller und Modelle hängen aneinander: Ein neuer Hersteller ändert die Auswahl im
  // Modell-Dialog, ein gelöschter Hersteller die Liste darunter. Deshalb lädt jede Änderung
  // beide Listen neu und nicht nur die eigene.
  const createHersteller = useCallback(
    async (name: string) => {
      const angelegt = await anlagentypenApi.createHersteller(name);
      await load();
      return angelegt;
    },
    [load],
  );

  const updateHersteller = useCallback(
    async (id: string, name: string) => {
      const geaendert = await anlagentypenApi.updateHersteller(id, name);
      await load();
      return geaendert;
    },
    [load],
  );

  const removeHersteller = useCallback(
    async (id: string) => {
      await anlagentypenApi.deleteHersteller(id);
      await load();
    },
    [load],
  );

  return {
    anlagentypen,
    hersteller,
    loading,
    error,
    reload: load,
    create,
    update,
    remove,
    createHersteller,
    updateHersteller,
    removeHersteller,
  };
};

/**
 * Stoffliste und Betriebsmodi eines Anlagentyps.
 *
 * Getrennt vom Hook oben, weil sie erst beim Öffnen eines Typs gebraucht werden – und weil ein
 * neu angelegter Typ noch keine hat: Kindzeilen brauchen einen Elternteil.
 */
export const useAnlagentypListen = (anlagentypId: string | null) => {
  const [stoffe, setStoffe] = useState<AnlagentypStoff[]>([]);
  const [betriebsmodi, setBetriebsmodi] = useState<AnlagentypBetriebsmodus[]>(
    [],
  );
  // Der Anlagentyp ist der Schlüssel: Beim Wechsel gehören die Zeilen zu einem anderen Typ und
  // dürfen nicht stehen bleiben. Das Neuladen nach jeder Änderung dagegen lässt sie stehen.
  const { loading, ladenStarten, ladenBeenden } = useLoadState(anlagentypId);

  const load = useCallback(async () => {
    if (!anlagentypId) {
      setStoffe([]);
      setBetriebsmodi([]);
      ladenBeenden();
      return;
    }
    ladenStarten();
    try {
      const [s, m] = await Promise.all([
        anlagentypenApi.getStoffe(anlagentypId),
        anlagentypenApi.getBetriebsmodi(anlagentypId),
      ]);
      setStoffe(s);
      setBetriebsmodi(m);
    } finally {
      ladenBeenden();
    }
  }, [anlagentypId, ladenStarten, ladenBeenden]);

  useEffect(() => {
    void load();
  }, [load]);

  const createStoff = useCallback(
    async (werte: StoffCreate) => {
      if (!anlagentypId) return;
      await anlagentypenApi.createStoff(anlagentypId, werte);
      await load();
    },
    [anlagentypId, load],
  );

  const updateStoff = useCallback(
    async (stoffId: string, werte: StoffUpdate) => {
      await anlagentypenApi.updateStoff(stoffId, werte);
      await load();
    },
    [load],
  );

  const deleteStoff = useCallback(
    async (stoffId: string) => {
      await anlagentypenApi.deleteStoff(stoffId);
      await load();
    },
    [load],
  );

  const createBetriebsmodus = useCallback(
    async (werte: BetriebsmodusCreate) => {
      if (!anlagentypId) return;
      await anlagentypenApi.createBetriebsmodus(anlagentypId, werte);
      await load();
    },
    [anlagentypId, load],
  );

  const updateBetriebsmodus = useCallback(
    async (modusId: string, werte: BetriebsmodusUpdate) => {
      await anlagentypenApi.updateBetriebsmodus(modusId, werte);
      await load();
    },
    [load],
  );

  const deleteBetriebsmodus = useCallback(
    async (modusId: string) => {
      await anlagentypenApi.deleteBetriebsmodus(modusId);
      await load();
    },
    [load],
  );

  return {
    stoffe,
    betriebsmodi,
    loading,
    createStoff,
    updateStoff,
    deleteStoff,
    createBetriebsmodus,
    updateBetriebsmodus,
    deleteBetriebsmodus,
  };
};
